import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/utils/tailwind-utils";

interface MRevealSectionProps {
  children: React.ReactNode;
  className?: string;
  /** Extra delay (seconds) on top of the base transition — use to stagger siblings. */
  delay?: number;
}

/**
 * Fades + slides a section into place the first time it enters the viewport.
 * Sections already on screen when the parent becomes ready animate in
 * immediately (staggered via `delay`); sections further down the page reveal
 * as the user scrolls to them.
 *
 * Always renders as a full-width, horizontally-centering flex box — several
 * dashboard/member-detail sections size themselves to less than 100% (e.g.
 * `width: 90%`) and rely on a centering ancestor for their side insets;
 * without `justify-center` here that centering context is lost and the
 * section flushes to the left edge instead.
 */
const MRevealSection: React.FC<MRevealSectionProps> = ({
  children,
  className,
  delay = 0,
}) => {
  return (
    <motion.div
      className={cn("flex w-full justify-center", className)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
};

export default MRevealSection;
